import * as React from "react";
import { showErrorToast, translateSubElementString } from "../../../../../../../utils";
import CategoryPreviewWrapper from "./CategoryPreviewWrapper";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import { DataTable } from "../../../../../../../components/DataTable";
import {
  Center,
  Flex,
  IconButton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { NumberField } from "../../../../../../../components/NumberField";
import {
  useGetWasteGeneratedInOperations
} from "../../../../../../../hooks/query/reports";
import { hideAppSpinner } from "../../../../../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { FaExclamationCircle } from "react-icons/fa";
import { ReviewDataModal } from "../ReviewDataModal";
import { useAuth } from "../../../../../../../context";
import { ReportsApi } from "../../../../../../../api";
import { fetchAllPages, plainExportHeader } from "../../../../../../../utils/fetchAllPages";

const numberOrNull = (value) => (value === null || value === undefined || value === "" ? null : Number(value));

function WastewaterTab({ data }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "reportsPage.scopeThree.inputs.wasteGeneratedInOperations.wastewater"
  });
  const { t: globalTranslation } = useTranslation("translation");

  const columns = useMemo(() => [
      {
        header: t("gridColumns.usageType"),
        accessorKey: "usageType",
        size: 350
      },
      {
        header: `${t("gridColumns.totalConsumption")} (${globalTranslation("measures.cubicMeters")})`,
        accessorKey: "totalConsumption",
        size: 260
      },
      {
        header: translateSubElementString(
          "reportsPage.scopeThree.inputs.wasteGeneratedInOperations.wastewater.gridColumns.totalEmissions"),
        accessorKey: "totalEmissions",
        size: 250
      }
    ],
    [t, globalTranslation]
  );

  const mapData = (data) => {
    return data && data.wastewater ? [{
      usageType: t("wastewater"),
      totalConsumption: data.wastewater.wastewaterConsumption ?? "",
      totalEmissions: data.wastewater.wastewaterEmission ?? ""
    }] : [];
  };

  return (
    <DataTable
      data={mapData(data)}
      columns={columns}
      containerHeight="340px"
    />
  );
}

function WasteGeneratedInOperationsTab({ reportId, currentElement }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "reportsPage.scopeThree.inputs.wasteGeneratedInOperations"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const questionnaireKeyPrefix = "organizationQuestionnaire.scope3.inputs.wasteGeneratedInOperations";
  const { t: questionnaireTranslation, i18n } = useTranslation("translation", {
    keyPrefix: questionnaireKeyPrefix
  });
  const dispatch = useDispatch();
  const toast = useToast();
  const {
    isOpen: isReviewModalOpen,
    onOpen: onReviewModalOpen,
    onClose: onReviewModalClose
  } = useDisclosure();

  const [dataList, setDataList] = useState([]);
  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [reviewData, setReviewData] = useState(null);

  const [dataTableState, setDataTableState] = useState({
    queryPageIndex: 0,
    queryPageSize: 10,
    // Items are already pre-sorted by the backend by "wasteActivity", "wasteType" and "disposalMethod"
    queryPageSortBy: [],
    totalElements: 0,
    totalPages: 0
  });

  const { totalPages, queryPageIndex, queryPageSize, queryPageSortBy, totalElements } = dataTableState;

  const manageDataTableState = (newValObject) => {
    setDataTableState((prevState) => {
      return {
        ...prevState,
        ...newValObject
      };
    });
  };

  const columns = useMemo(() => {
      const handleReviewData = (attribute) => {
        onReviewModalOpen();
        setSelectedAttribute(attribute);
        if (attribute) {
          setReviewData(currentElement[`${attribute}Items`].map(el => {
            return {
              ...el,
              [attribute]: i18n.exists(`${questionnaireKeyPrefix}.${attribute}.options.${el[attribute] ?? ""}`) ?
                questionnaireTranslation(`${attribute}.options.${el[attribute]}`) : el[attribute] ?? ""
            };
          }));
        }
      };

      return [
        {
          header: <Flex alignItems="center">
            {t("wasteActivity")}
            <Tooltip
              label={t("reviewData", { attribute: t("wasteActivity") })}
              whiteSpace={"pre-line"}
              hasArrow
            >
              <IconButton
                aria-label="wasteActivityInfo"
                type="button"
                icon={<FaExclamationCircle size="20" />}
                flexShrink="0"
                size="sm"
                opacity="0.25"
                tabIndex={-1}
                onClick={() => {
                  handleReviewData("wasteActivity");
                }}
              />
            </Tooltip>
          </Flex>,
          accessorKey: "wasteActivity",
          meta: { exportHeader: t("wasteActivity") },
          size: 150
        },
        {
          header: t("wasteType"),
          accessorKey: "wasteType",
          size: 180,
          customStyles: {
            verticalAlign: "middle"
          }
        },
        {
          header: <Flex alignItems="center">
            {t("disposalMethod")}
            <Tooltip
              label={t("reviewData", { attribute: t("disposalMethod") })}
              whiteSpace={"pre-line"}
              hasArrow
            >
              <IconButton
                aria-label="disposalMethodInfo"
                type="button"
                icon={<FaExclamationCircle size="20" />}
                flexShrink="0"
                size="sm"
                opacity="0.25"
                tabIndex={-1}
                onClick={() => {
                  handleReviewData("disposalMethod");
                }}
              />
            </Tooltip>
          </Flex>,
          accessorKey: "disposalMethod",
          meta: { exportHeader: t("disposalMethod") },
          size: 170
        },
        {
          header: t("weight"),
          accessorKey: "weight",
          size: 130,
          customStyles: {
            verticalAlign: "middle"
          }
        },
        {
          header: translateSubElementString("reportsPage.scopeThree.inputs.wasteGeneratedInOperations.emissions"),
          accessorKey: "emissions",
          meta: { exportHeader: plainExportHeader(globalTranslation("reportsPage.scopeThree.inputs.wasteGeneratedInOperations.emissions")) },
          size: 130,
          customStyles: {
            verticalAlign: "middle"
          }
        }
      ];
    },
    [t, globalTranslation, onReviewModalOpen, i18n, questionnaireTranslation, currentElement]
  );

  const handleReviewModalClose = () => {
    setSelectedAttribute(null);
    setReviewData(null);
    onReviewModalClose();
  };

  // One mapping for the page on screen and for Export; the export keeps the numbers numeric so Excel can sum them.
  const toRow = useCallback(
    (item, { forExport = false } = {}) => ({
      id: item.id,
      wasteActivity: i18n.exists(`${questionnaireKeyPrefix}.wasteActivity.options.${item.wasteActivity ?? ""}`) ?
        questionnaireTranslation(`wasteActivity.options.${item.wasteActivity}`) : item.wasteActivity ?? "",
      wasteType: i18n.exists(`${questionnaireKeyPrefix}.wasteType.options.${item.wasteActivity}.${item.wasteType ?? ""}`) ?
        questionnaireTranslation(`wasteType.options.${item.wasteActivity}.${item.wasteType}`) : item.wasteType ?? "",
      disposalMethod: i18n.exists(`${questionnaireKeyPrefix}.disposalMethod.options.${item.disposalMethod ?? ""}`) ?
        questionnaireTranslation(`disposalMethod.options.${item.disposalMethod}`) : item.disposalMethod ?? "",
      weight: forExport ? numberOrNull(item.weight) : Number(item.weight)?.toLocaleString("en-GB", { maximumFractionDigits: 4 }),
      emissions: forExport ? numberOrNull(item.co2Emissions) : Number(item.co2Emissions)?.toLocaleString("en-GB", { maximumFractionDigits: 4 })
    }),
    [i18n, questionnaireKeyPrefix, questionnaireTranslation]
  );

  const { callApi } = useAuth();
  // Every row, not the page on screen, in the same order the table shows.
  const fetchAllRowsForExport = () =>
    fetchAllPages(({ page, size }) =>
      callApi(ReportsApi.getWasteGeneratedInOperations, { reportId, params: { page, size, sort: queryPageSortBy } })
    ).then((items) => items.map((item) => toRow(item, { forExport: true })));

  const {
    isLoading,
    isFetching
  } = useGetWasteGeneratedInOperations(
    reportId,
    { page: queryPageIndex, size: queryPageSize, sort: queryPageSortBy },
    {
      onSuccess: (page) => {
        const pageContent = page?.content ?? [];
        const dataItems = pageContent.map((item) => toRow(item));

        setDataList(dataItems);
        if (pageContent.length === 0 && page?.number !== 0 && page?.totalElements !== 0 && queryPageIndex > 0) {
          // Case when page size is changed to larger and user has to be redirected to the previous page
          manageDataTableState({
            totalElements: page?.totalElements ?? 0,
            totalPages: page?.totalPages ?? 0,
            queryPageIndex: queryPageIndex - 1
          });
        } else {
          manageDataTableState({
            totalElements: page?.totalElements ?? 0,
            totalPages: page?.totalPages ?? 0
          });
        }
      },
      onError: err => showErrorToast(toast, err.message),
      onSettled: () => {
        dispatch(hideAppSpinner());
      },
      cacheTime: 0
    }
  );

  return (
    <>
      <DataTable
        data={dataList}
        columns={columns}
        isLoading={isLoading || isFetching}
        containerHeight={dataList && dataList.length > 0 ? "370px" : null}
        marginBlockEnd={8}
        serverSidePagination={true}
        fetchAllRowsForExport={fetchAllRowsForExport}
        queryTotalCount={totalElements}
        queryPageCount={totalPages}
        queryPageIndex={queryPageIndex}
        setQueryPageIndex={(value) => manageDataTableState({ queryPageIndex: value })}
        queryPageSize={queryPageSize}
        setQueryPageSize={(value) => manageDataTableState({ queryPageSize: value })}
      />
      {
        isReviewModalOpen && selectedAttribute !== null && reviewData !== null && (
          <ReviewDataModal
            isOpen={isReviewModalOpen}
            onClose={handleReviewModalClose}
            dataAttribute={selectedAttribute}
            gridHeader={t(selectedAttribute)}
            data={reviewData}
          />
        )
      }
    </>
  );
}

function WasteGeneratedInOperationsPreview({ reportId, currentElement }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "reportsPage.scopeThree.inputs.wasteGeneratedInOperations.tabs"
  });
  const [tabIndex, setTabIndex] = useState(0);

  return (
    <CategoryPreviewWrapper>
      <Flex marginBlockEnd={4}>
        <Text size="sm" fontWeight="bold">
          {translateSubElementString(
            "reportsPage.scopeThree.inputs.mainTotalEmissions"
          )}
        </Text>
        <NumberField value={currentElement?.totalCo2Emissions} marginStart="2" />
      </Flex>
      <Tabs
        colorScheme="customScheme"
        index={tabIndex}
        onChange={setTabIndex}
        marginBlockStart="4"
        height="calc(100% - var(--chakra-space-12))"
        overflow="auto"
      >
        <Center>
          <TabList borderBottom="none" marginBottom="4">
            <Tab _focus={{ boxShadow: "none" }}>{t("wastewaterTab")}</Tab>
            <Tab _focus={{ boxShadow: "none" }}>{t("wasteGeneratedInOperationsTab")}</Tab>
          </TabList>
        </Center>
        <TabPanels maxHeight="100%">
          <TabPanel padding="0">
            <WastewaterTab data={currentElement} />
          </TabPanel>
          <TabPanel padding="0">
            <WasteGeneratedInOperationsTab reportId={reportId} currentElement={currentElement} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </CategoryPreviewWrapper>
  );
}

export default WasteGeneratedInOperationsPreview;